import { usePortfolioSimilarity } from "utils/clientAPIs/portfolio";
import { useEffect, useState } from "react";
import { useRef } from "react";

/**
 * 수평 스크롤 방향 타입
 */
type ScrollDirection = 'none' | 'horizontal' | 'vertical';

/**
 * 스크롤 임계값 설정
 */
const SCROLL_THRESHOLD = 10; // px
const DIRECTION_THRESHOLD = 8; // px - 방향 감지를 위한 최소 거리

export const usePortfolioSimUtil = (portId: number) => {
    const [hashId, setHashId] = useState<string | null | undefined>(undefined);
    const { data, isLoading, isError } = usePortfolioSimilarity(Number(portId), hashId);
    useEffect(() => {
        const hashId = localStorage.getItem('pf-hashId');
        setHashId(hashId ?? null);
    }, []);

    const yearLogReturn = data?.yearLogReturn;
    const itemInfo = data?.itemInfo;
    const similarity = data?.similarity || String(data?.similarity) === "0" ? Number(data?.similarity.toFixed(1)) : null;
    const averageSimilarity = data?.averageSimilarity || String(data?.averageSimilarity) === "0" ? Number(data?.averageSimilarity.toFixed(1)) : null;

    return { yearLogReturn, itemInfo, similarity, averageSimilarity, isLoading, isError };
};


export const useHorizontalScroll = (data: any, activeIndex: number, setActiveIndex: (index: number) => void, iconSize = 30, gap = 4) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('none');
    const [initialTouchHandled, setInitialTouchHandled] = useState(false);
    const [isMouseDragging, setIsMouseDragging] = useState(false); // 마우스 전용 드래그 상태

    const [notiTextVisible, setNotiTextVisible] = useState(true);

    const minScroll = -(data?.length * iconSize + data?.length * gap);

    useEffect(() => {
        if (activeIndex) setNotiTextVisible(false);
    }, [activeIndex]);

    // 방향성 감지 함수 (터치 전용)
    const detectScrollDirection = (deltaX: number, deltaY: number): ScrollDirection => {
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        // 임계값보다 작으면 방향 미결정
        if (absDeltaX < DIRECTION_THRESHOLD && absDeltaY < DIRECTION_THRESHOLD) {
            return 'none';
        }

        // 수평 움직임이 더 클 경우
        if (absDeltaX > absDeltaY * 1.5) {
            return 'horizontal';
        }

        // 수직 움직임이 더 클 경우
        if (absDeltaY > absDeltaX * 1.5) {
            return 'vertical';
        }

        return 'none';
    };

    // 터치 이벤트 핸들러 - 방향성 감지 적용
    const handleTouchStart = (clientX: number, clientY: number) => {
        setStartX(clientX);
        setStartY(clientY);
        setScrollDirection('none');
        setInitialTouchHandled(false);
        setIsMouseDragging(false);
        // 터치는 방향이 결정된 후에 드래그 시작
    };

    const handleTouchMove = (clientX: number, clientY: number, event?: Event) => {
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;

        // 방향이 아직 결정되지 않은 경우
        if (scrollDirection === 'none' && !initialTouchHandled) {
            const detectedDirection = detectScrollDirection(deltaX, deltaY);

            if (detectedDirection !== 'none') {
                setScrollDirection(detectedDirection);
                setInitialTouchHandled(true);

                // 수평 방향일 때만 드래그 시작
                if (detectedDirection === 'horizontal') {
                    setIsDragging(true);
                    // 수평 스크롤을 위해 기본 동작 방지
                    if (event) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                } else {
                    // 수직 방향이면 상위 컴포넌트가 처리하도록 허용
                    return;
                }
            } else {
                // 방향이 결정되지 않았으면 아직 대기
                return;
            }
        }

        // 수직 스크롤이 감지된 경우 처리하지 않음
        if (scrollDirection === 'vertical' || !isDragging) return;

        // 수평 스크롤 처리
        if (scrollDirection === 'horizontal') {
            const newTranslateX = translateX + deltaX;
            const maxScroll = 0; // 오른쪽 끝

            const clampedTranslateX = Math.max(
                minScroll,
                Math.min(maxScroll, newTranslateX)
            );
            setTranslateX(clampedTranslateX);
            setStartX(clientX);

            // 드래그 중에도 실시간으로 중앙 아이콘 감지
            updateActiveIndex();
        }
    };

    // 마우스 이벤트 핸들러 - 즉시 드래그 시작
    const handleMouseStart = (clientX: number, clientY: number) => {
        setStartX(clientX);
        setStartY(clientY);
        setScrollDirection('horizontal'); // 마우스는 즉시 수평으로 설정
        setInitialTouchHandled(true);
        setIsDragging(true);
        setIsMouseDragging(true);
    };

    const handleMouseMove = (clientX: number, clientY: number) => {
        if (!isMouseDragging || !isDragging) return;

        const deltaX = clientX - startX;
        const newTranslateX = translateX + deltaX;
        const maxScroll = 0; // 오른쪽 끝

        const clampedTranslateX = Math.max(
            minScroll,
            Math.min(maxScroll, newTranslateX)
        );
        setTranslateX(clampedTranslateX);
        setStartX(clientX);

        // 드래그 중에도 실시간으로 중앙 아이콘 감지
        updateActiveIndex();
    };

    const handleEnd = () => {
        if (isDragging && (scrollDirection === 'horizontal' || isMouseDragging)) {
            // 드래그가 끝났을 때 가장 중앙에 있는 아이콘을 찾아서 activeIndex 설정하고 스크롤 위치 보정
            updateActiveIndex();
            // 스크롤 위치 자동 보정
            snapToClosestIcon();
        }

        setIsDragging(false);
        setScrollDirection('none');
        setInitialTouchHandled(false);
        setIsMouseDragging(false);
    };

    // 가장 중앙에 있는 아이콘의 인덱스를 찾아서 activeIndex 설정
    const updateActiveIndex = () => {
        if (!containerRef.current || !scrollerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerCenterX = containerRect.left + containerRect.width / 2;

        const iconElements = scrollerRef.current.querySelectorAll(".scroller-icon");
        let closestIndex = 0;
        let minDistance = Infinity;

        iconElements.forEach((iconElement, index) => {
            const iconRect = iconElement.getBoundingClientRect();
            const iconCenterX = iconRect.left + iconRect.width / 2;
            const distance = Math.abs(iconCenterX - containerCenterX);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
            }
        });

        setActiveIndex(closestIndex);
    };

    // 특정 인덱스의 아이콘을 중앙에 위치시키는 함수
    const scrollToIndex = (targetIndex: number) => {
        if (!containerRef.current || !scrollerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerCenterX = containerRect.left + containerRect.width / 2;

        const iconElements = scrollerRef.current.querySelectorAll(".scroller-icon");
        const targetIcon = iconElements[targetIndex] as HTMLElement;

        if (!targetIcon) return;

        const iconRect = targetIcon.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;

        // 아이콘의 중앙과 컨테이너의 중앙 사이의 거리 계산
        const distanceToCenter = containerCenterX - iconCenterX;

        // 현재 translateX에 거리를 더해서 새로운 위치 계산
        const newTranslateX = translateX + distanceToCenter;

        // 스크롤 범위 제한
        const maxScroll = 0;

        const clampedTranslateX = Math.max(
            minScroll,
            Math.min(maxScroll, newTranslateX)
        );

        setTranslateX(clampedTranslateX);
        setActiveIndex(targetIndex);
    };

    // 가장 가까운 아이콘으로 스크롤 위치를 자동 보정하는 함수
    const snapToClosestIcon = () => {
        if (!containerRef.current || !scrollerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerCenterX = containerRect.left + containerRect.width / 2;

        const iconElements = scrollerRef.current.querySelectorAll(".scroller-icon");
        let closestIndex = 0;
        let minDistance = Infinity;

        // 가장 중앙에 가까운 아이콘 찾기
        iconElements.forEach((iconElement, index) => {
            const iconRect = iconElement.getBoundingClientRect();
            const iconCenterX = iconRect.left + iconRect.width / 2;
            const distance = Math.abs(iconCenterX - containerCenterX);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
            }
        });

        // 가장 가까운 아이콘을 중앙에 위치시키기
        const targetIcon = iconElements[closestIndex] as HTMLElement;
        if (!targetIcon) return;

        const iconRect = targetIcon.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;

        // 아이콘의 중앙과 컨테이너의 중앙 사이의 거리 계산
        const distanceToCenter = containerCenterX - iconCenterX;

        // 현재 translateX에 거리를 더해서 새로운 위치 계산
        const newTranslateX = translateX + distanceToCenter;

        // 스크롤 범위 제한
        const maxScroll = 0;

        const clampedTranslateX = Math.max(
            minScroll,
            Math.min(maxScroll, newTranslateX)
        );

        setTranslateX(clampedTranslateX);
        setActiveIndex(closestIndex);
    };

    // 터치 이벤트 (모바일) - 방향성 감지 적용
    const handleTouchStartEvent = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        handleTouchStart(touch.clientX, touch.clientY);
    };

    const handleTouchMoveEvent = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        handleTouchMove(touch.clientX, touch.clientY, e.nativeEvent);
    };

    const handleTouchEndEvent = (e: React.TouchEvent) => {
        handleEnd();
    };

    // 마우스 이벤트 (데스크탑) - 즉시 드래그 시작
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleMouseStart(e.clientX, e.clientY);
    };

    const handleMouseMoveEvent = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleMouseMove(e.clientX, e.clientY);
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleEnd();
    };

    // 전역 마우스 이벤트 리스너
    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isMouseDragging && isDragging) {
                e.preventDefault();
                handleMouseMove(e.clientX, e.clientY);
            }
        };

        const handleGlobalMouseUp = () => {
            if (isMouseDragging) {
                handleEnd();
            }
        };

        if (isMouseDragging && isDragging) {
            document.addEventListener("mousemove", handleGlobalMouseMove);
            document.addEventListener("mouseup", handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleGlobalMouseMove);
            document.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [isMouseDragging, isDragging, startX, translateX]);

    return {
        containerRef,
        scrollerRef,
        isDragging,
        handleTouchStart: handleTouchStartEvent,
        handleTouchMove: handleTouchMoveEvent,
        handleTouchEnd: handleTouchEndEvent,
        handleMouseDown,
        handleMouseMove: handleMouseMoveEvent,
        handleMouseUp,
        translateX,
        scrollToIndex,
        notiTextVisible,
        scrollDirection, // 디버깅용으로 추가
    }
}